import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import api, { API } from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Paperclip, Upload, FolderOpen, Download, Play, Square, Clock } from "lucide-react";
import TaskCommentsPanel from "./TaskCommentsDialog";

export const fmtHours = (h) => `${Math.floor(h)}h ${String(Math.round((h - Math.floor(h)) * 60)).padStart(2, "0")}m`;

function TimeTab({ task, onChanged }) {
  const { t, user } = useApp();
  const [entries, setEntries] = useState(task.time_entries || []);
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [tick, setTick] = useState(0);
  const running = entries.find((e) => !e.end && e.user_id === user?.user_id);
  useEffect(() => { if (!running) return; const i = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(i); }, [running]);
  const total = entries.filter((e) => e.end).reduce((s, e) => s + (e.seconds || 0), 0) / 3600;
  const value = Number(task.amount) > 0 ? Number(task.amount) : total * Number(task.hourly_rate || 0);
  const refresh = async () => { const r = await api.get("/tasks"); const tk = r.data.find((x) => x.id === task.id); if (tk) setEntries(tk.time_entries || []); onChanged(); };
  const call = async (fn) => { try { await fn(); await refresh(); } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); } };
  const elapsed = running ? Math.max(0, Math.floor((Date.now() - new Date(running.start).getTime()) / 1000)) : 0;
  void tick;
  return (
    <div className="space-y-3" data-testid="task-time">
      <div className="flex items-center justify-between rounded-md border border-border p-3">
        <div>
          <div className="text-xs text-muted-foreground">{t("task.totalHours")}</div>
          <div className="font-mono text-lg" data-testid="task-time-total">{fmtHours(total)}</div>
          {(task.hourly_rate > 0 || task.amount > 0) && <div className="text-[10px] text-muted-foreground">{t("task.value")}: <span className="font-mono">{value.toFixed(2)} €</span>{task.hourly_rate > 0 && !(task.amount > 0) && ` (${task.hourly_rate} €/h)`}</div>}
        </div>
        {running ? (
          <Button variant="destructive" onClick={() => call(() => api.post(`/tasks/${task.id}/timer/stop`))} data-testid="timer-stop-button">
            <Square className="h-4 w-4 mr-2" />{t("task.timerStop")} <span className="font-mono ml-2">{fmtHours(elapsed / 3600)}</span>
          </Button>
        ) : (
          <Button onClick={() => call(() => api.post(`/tasks/${task.id}/timer/start`))} data-testid="timer-start-button"><Play className="h-4 w-4 mr-2" />{t("task.timerStart")}</Button>
        )}
      </div>
      <div className="space-y-1 max-h-[32vh] overflow-y-auto">
        {entries.length === 0 && <div className="text-sm text-muted-foreground py-3 text-center">{t("task.noTime")}</div>}
        {[...entries].reverse().map((e) => (
          <div key={e.id} className="flex items-center gap-2 text-xs rounded px-2 py-1.5 hover:bg-muted/50" data-testid={`time-entry-${e.id}`}>
            <Clock className={`h-3 w-3 shrink-0 ${e.end ? "text-muted-foreground" : "text-emerald-500 animate-pulse"}`} />
            <span className="flex-1 truncate">{e.user_name}{e.note ? ` · ${e.note}` : ""}</span>
            <span className="text-muted-foreground font-mono">{e.start.slice(0, 16).replace("T", " ")}</span>
            <span className="font-mono w-16 text-right">{e.end ? fmtHours((e.seconds || 0) / 3600) : "…"}</span>
            {e.end && (e.user_id === user?.user_id || ["master", "admin"].includes(user?.role)) && (
              <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => call(() => api.delete(`/tasks/${task.id}/time/${e.id}`))} data-testid={`time-delete-${e.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 items-end border-t border-border pt-3">
        <div className="w-24"><Label className="text-xs">{t("task.minutes")}</Label><Input type="number" min="1" value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="time-minutes-input" /></div>
        <div className="flex-1"><Label className="text-xs">{t("task.note")}</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="time-note-input" /></div>
        <Button variant="outline" disabled={!Number(minutes)} onClick={() => call(async () => { await api.post(`/tasks/${task.id}/time`, { minutes: Number(minutes), note }); setMinutes(""); setNote(""); })} data-testid="time-add-button"><Plus className="h-4 w-4 mr-1" />{t("task.addTime")}</Button>
      </div>
    </div>
  );
}

export const checklistStats = (task) => {
  const items = task.checklist || [];
  const done = items.filter((i) => i.done).length;
  return { total: items.length, done, pct: items.length ? Math.round((done / items.length) * 100) : 0 };
};

function ChecklistTab({ task, onChanged }) {
  const { t } = useApp();
  const [items, setItems] = useState(task.checklist || []);
  const [text, setText] = useState("");
  const save = async (next) => {
    setItems(next);
    try { await api.put(`/tasks/${task.id}/checklist`, { items: next }); onChanged(); }
    catch { toast.error(t("msg.error")); }
  };
  const add = () => { if (!text.trim()) return; save([...items, { id: "", text: text.trim(), done: false }]); setText(""); };
  const { done, total, pct } = checklistStats({ checklist: items });
  return (
    <div className="space-y-3" data-testid="task-checklist">
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <Progress value={pct} className="h-2 flex-1" data-testid="task-checklist-progress" />
        <span className="font-mono" data-testid="task-checklist-count">{done}/{total}</span>
      </div>
      <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
        {items.map((it, idx) => (
          <div key={it.id || idx} className="flex items-center gap-2 group" data-testid={`checklist-item-${idx}`}>
            <Checkbox checked={it.done} onCheckedChange={(v) => save(items.map((x, i) => (i === idx ? { ...x, done: !!v } : x)))} data-testid={`checklist-toggle-${idx}`} />
            <span className={`flex-1 text-sm ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.text}</span>
            <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => save(items.filter((_, i) => i !== idx))} data-testid={`checklist-remove-${idx}`}>
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("task.addItem")} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="checklist-new-input" />
        <Button onClick={add} disabled={!text.trim()} data-testid="checklist-add-button"><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function AttachmentsTab({ task, onChanged }) {
  const { t, user, entities } = useApp();
  const [atts, setAtts] = useState(task.attachments || []);
  const [vaultDocs, setVaultDocs] = useState([]);
  const [vaultEntity, setVaultEntity] = useState(task.entity_id || entities[0]?.entity_id || "");
  const [picking, setPicking] = useState(false);
  const fileRef = React.useRef(null);
  const isViewer = user?.role === "viewer";

  useEffect(() => {
    if (!picking || isViewer || !vaultEntity) return;
    api.get(`/vault?entity_id=${vaultEntity}`).then((r) => setVaultDocs(r.data)).catch(() => setVaultDocs([]));
  }, [picking, vaultEntity, isViewer]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/tasks/${task.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtts((a) => [...a, r.data]); onChanged(); toast.success(t("msg.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const linkVault = async (vid) => {
    try {
      const r = await api.post(`/tasks/${task.id}/attachments/vault`, { vault_id: vid });
      setAtts((a) => [...a, r.data]); onChanged(); setPicking(false); toast.success(t("msg.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
  };
  const remove = async (aid) => {
    try { await api.delete(`/tasks/${task.id}/attachments/${aid}`); setAtts((a) => a.filter((x) => x.id !== aid)); onChanged(); }
    catch { toast.error(t("msg.error")); }
  };

  return (
    <div className="space-y-3" data-testid="task-attachments">
      <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
        {atts.length === 0 && <div className="text-sm text-muted-foreground py-3 text-center">{t("task.noAttachments")}</div>}
        {atts.map((a) => (
          <div key={a.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm" data-testid={`attachment-${a.id}`}>
            {a.kind === "vault" ? <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" /> : <Paperclip className="h-4 w-4 text-primary shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="truncate">{a.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{a.kind === "vault" ? `Vault · ${a.subject_name || a.doc_type}` : `${Math.round((a.size || 0) / 1024)} KB`} · {a.added_by}</div>
            </div>
            {a.storage_path && (
              <a href={`${API}/tasks/${task.id}/attachments/${a.id}/download`} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-muted" data-testid={`attachment-download-${a.id}`}>
                <Download className="h-4 w-4" />
              </a>
            )}
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => remove(a.id)} data-testid={`attachment-remove-${a.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="attachment-file-input" />
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="attachment-upload-button"><Upload className="h-4 w-4 mr-1" />{t("task.uploadFile")}</Button>
        {!isViewer && <Button size="sm" variant="outline" onClick={() => setPicking((p) => !p)} data-testid="attachment-vault-button"><FolderOpen className="h-4 w-4 mr-1" />{t("task.fromVault")}</Button>}
      </div>
      {picking && !isViewer && (
        <div className="rounded-md border border-border p-2 space-y-2" data-testid="vault-picker">
          <Select value={vaultEntity} onValueChange={setVaultEntity}>
            <SelectTrigger className="h-8" data-testid="vault-picker-entity"><SelectValue /></SelectTrigger>
            <SelectContent>{entities.map((e) => <SelectItem key={e.entity_id} value={e.entity_id}>{e.code}</SelectItem>)}</SelectContent>
          </Select>
          <div className="max-h-40 overflow-y-auto divide-y divide-border">
            {vaultDocs.length === 0 && <div className="text-xs text-muted-foreground p-2">{t("common.noData")}</div>}
            {vaultDocs.map((d) => (
              <button key={d.id} type="button" onClick={() => linkVault(d.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted flex justify-between gap-2" data-testid={`vault-pick-${d.id}`}>
                <span className="truncate">{d.original_filename}</span>
                <span className="text-muted-foreground truncate">{d.subject_name || d.doc_type}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ task }) {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/tasks/${task.id}/activity`).then((r) => setItems(r.data)).catch(() => {}); }, [task.id]);
  const COL = { todo: t("task.col.todo"), doing: t("task.col.doing"), review: t("task.col.review"), done: t("task.col.done") };
  return (
    <div className="space-y-0 max-h-[50vh] overflow-y-auto" data-testid="task-history">
      {items.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">{t("task.noHistory")}</div>}
      {items.map((a) => (
        <div key={a.id} className="relative pl-5 pb-3 text-xs border-l border-border ml-2" data-testid={`activity-${a.id}`}>
          <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-primary" />
          <div><span className="font-medium text-foreground">{a.actor_name}</span> <span className="text-muted-foreground">{t(`act.${a.action}`)}</span>          {a.action === "moved" ? ` ${COL[a.detail] || a.detail}` : ""}</div>
          {a.detail && a.action !== "moved" && <div className="text-muted-foreground truncate">{a.detail}</div>}
          <div className="text-[10px] text-muted-foreground/70 font-mono">{a.created_at.slice(0, 16).replace("T", " ")}</div>
        </div>
      ))}
    </div>
  );
}

export default function TaskDetailDialog({ task, onClose, onChanged, initialTab = "comments" }) {
  const { t } = useApp();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="task-detail-dialog">
        <DialogHeader><DialogTitle className="truncate">{task.title}</DialogTitle></DialogHeader>
        <Tabs defaultValue={initialTab}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="comments" data-testid="task-tab-comments">{t("task.comments")}</TabsTrigger>
            <TabsTrigger value="checklist" data-testid="task-tab-checklist">{t("task.checklist")}</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="task-tab-attachments">{t("task.attachments")}</TabsTrigger>
            <TabsTrigger value="time" data-testid="task-tab-time">{t("task.time")}</TabsTrigger>
            <TabsTrigger value="history" data-testid="task-tab-history">{t("task.history")}</TabsTrigger>
          </TabsList>
          <TabsContent value="time"><TimeTab task={task} onChanged={onChanged} /></TabsContent>
          <TabsContent value="history"><HistoryTab task={task} /></TabsContent>
          <TabsContent value="comments"><TaskCommentsPanel task={task} onChanged={onChanged} embedded /></TabsContent>
          <TabsContent value="checklist"><ChecklistTab task={task} onChanged={onChanged} /></TabsContent>
          <TabsContent value="attachments"><AttachmentsTab task={task} onChanged={onChanged} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
